import {
  forwardRef, useId, type InputHTMLAttributes, type ReactNode,
} from 'react'
import { AlertCircle, Check } from 'lucide-react'
import { cx } from './cx'
import s from './Choice.module.css'

export type ChoiceAppearance = 'plain' | 'card'

export interface ChoiceProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** `checkbox` (a cut square) or `radio` (round). */
  type?: 'checkbox' | 'radio'
  /** The option's words. The accessible name. */
  label: ReactNode
  /** The muted line under it: what choosing this MEANS ("Nobody outside the
   *  tenant can see this record"). Read as the option's description. */
  description?: ReactNode
  /** `card`: the option is a row in a recess and the chosen one is a seated
   *  plate. `plain`: a mark and its words. */
  appearance?: ChoiceAppearance
}

/**
 * ONE DESCRIBED CHOICE (#2406, "Input Controls and Validation": "Visibility —
 * Internal only / Nobody outside the tenant can see this record", "Options —
 * Notify the account owner").
 *
 * The native input IS the control — visually hidden, never removed, so it keeps
 * its tab stop, its Space toggle, the arrow keys a radio group gives for free,
 * form submission and `setChecked` in a test. The painted mark is decoration
 * over it (`aria-hidden`); the label wraps both, so a press anywhere on the
 * words toggles it. The description is the input's `aria-describedby`.
 *
 * READ-ONLY is a real state here, because the value is real and not editable
 * HERE — which a native checkbox cannot express (`readonly` is ignored on it).
 * So a read-only choice keeps its focus stop and its value, refuses the press,
 * and says `aria-readonly`. DISABLED is different: the option does not apply,
 * it leaves the tab order and goes flat. The two never draw the same.
 */
export const Choice = forwardRef<HTMLInputElement, ChoiceProps>(function Choice(
  {
    type = 'checkbox', label, description, appearance = 'plain', className, readOnly,
    checked, defaultChecked, onChange, ...rest
  },
  ref,
) {
  const auto = useId()
  const labelId = `${auto}-label`
  const descriptionId = `${auto}-description`
  const describedBy = [rest['aria-describedby'], description != null ? descriptionId : null]
    .filter(Boolean).join(' ') || undefined

  /* READ-ONLY refuses the change by HOLDING the value: the input is controlled
     at what it was given and the host's `onChange` is never called, so React
     restores the mark after any activation — a press on the words, Space, a
     click. `preventDefault` on the click was tried first and measured wrong: it
     races React's own restoration of a controlled checkbox and the mark came
     back UNCHECKED. */
  const held = readOnly
    ? { checked: Boolean(checked ?? defaultChecked), onChange: () => {} }
    : { checked, defaultChecked, onChange }

  return (
    <label className={cx(s.choice, s[appearance], className)} data-choice={type}
      data-readonly={readOnly || undefined}>
      <input
        {...rest}
        {...held}
        ref={ref}
        type={type}
        className={s.native}
        aria-readonly={readOnly || undefined}
        /* NAMED BY THE WORDS ALONE. The wrapping label would otherwise name the
           input with its whole text — the description too — so a reader would
           hear the sentence twice, once as the name and once as the
           description. */
        aria-labelledby={rest['aria-label'] != null ? undefined : [rest['aria-labelledby'], labelId].filter(Boolean).join(' ')}
        aria-describedby={describedBy}
      />
      <span className={cx(s.mark, type === 'radio' ? s.radio : s.checkbox)} aria-hidden="true">
        {type === 'checkbox' && <Check className={s.tick} strokeWidth={3} />}
      </span>
      <span className={s.text}>
        <span className={s.label} id={labelId}>{label}</span>
        {description != null && <span className={s.description} id={descriptionId}>{description}</span>}
      </span>
    </label>
  )
})

export interface ChoiceOption {
  value: string
  label: ReactNode
  description?: ReactNode
  disabled?: boolean
}

interface ChoiceGroupBase {
  /** The group's name — a `<legend>`. */
  legend: ReactNode
  options: readonly ChoiceOption[]
  /** Guidance for the whole group, announced with it. */
  hint?: ReactNode
  /** The group's validation message. Announced; drawn with a glyph. */
  error?: string
  appearance?: ChoiceAppearance
  disabled?: boolean
  readOnly?: boolean
  /** The `name` the inputs share. Defaults to a generated one. */
  name?: string
  className?: string
  testId?: string
}

export type ChoiceGroupProps = ChoiceGroupBase & (
  | { type: 'radio'; value: string | null; onChange: (value: string) => void; required?: boolean }
  | { type: 'checkbox'; value: readonly string[]; onChange: (value: string[]) => void; required?: never }
)

/**
 * A GROUP OF DESCRIBED CHOICES — radios or checkboxes under one legend.
 *
 * A real `<fieldset>` with a real `<legend>`, so the group is named by what it
 * asks and every option is announced inside it; same-name radios get the
 * browser's own arrow-key roving and single tab stop, which a hand-built
 * radiogroup has to re-implement and usually gets wrong. Its hint and error
 * are the fieldset's description, and the error is drawn with a glyph and
 * announced when it appears — never colour alone (`Field`'s rule, for a group).
 *
 * `appearance="card"` is the design's selected-plate form: the options sit in a
 * recess and the chosen one is a seated plate, the segmented control's
 * material, for a choice whose options carry a sentence each.
 */
export function ChoiceGroup(props: ChoiceGroupProps) {
  const {
    legend, options, hint, error, appearance = 'plain', disabled, readOnly, name, className, testId,
  } = props
  const auto = useId()
  const groupName = name ?? `${auto}-choice`
  const hintId = `${auto}-hint`
  const errorId = `${auto}-error`
  const describedBy = [hint != null ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined

  const isChecked = (value: string) => props.type === 'radio'
    ? props.value === value
    : props.value.includes(value)

  const toggle = (value: string, checked: boolean) => {
    if (props.type === 'radio') { if (checked) props.onChange(value); return }
    props.onChange(checked
      ? [...props.value.filter((held) => held !== value), value]
      : props.value.filter((held) => held !== value))
  }

  return (
    <fieldset
      className={cx(s.group, s[`group_${appearance}`], className)}
      aria-describedby={describedBy}
      disabled={disabled}
      data-testid={testId}
      data-invalid={error ? '' : undefined}
    >
      <legend className={s.legend}>{legend}</legend>
      <div className={s.options}>
        {options.map((option, index) => (
          <Choice
            key={option.value}
            type={props.type}
            name={groupName}
            value={option.value}
            label={option.label}
            description={option.description}
            appearance={appearance}
            checked={isChecked(option.value)}
            disabled={option.disabled}
            readOnly={readOnly}
            required={props.type === 'radio' && props.required && index === 0 ? true : undefined}
            aria-invalid={error ? true : undefined}
            onChange={(event) => toggle(option.value, event.target.checked)}
          />
        ))}
      </div>
      {hint != null && <div className={s.hint} id={hintId}>{hint}</div>}
      {error && (
        <div className={s.error} id={errorId} role="alert">
          <AlertCircle className={s.errorGlyph} aria-hidden />
          <span>{error}</span>
        </div>
      )}
    </fieldset>
  )
}
